import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X } from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import {
  TCreateOnboardingQuestionRequest,
  TOnboardingQuestion,
  TUpdateOnboardingQuestionRequest,
} from "@/api/admin/admin.types";
import { Form, FormButton, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/forms";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";

const questionFormSchema = z
  .object({
    question_text: z
      .string()
      .min(1, "Question text is required")
      .max(2000, "Question text must be less than 2000 characters"),
    question_description: z.string().optional(),
    data_type: z.enum([
      "text",
      "file_upload",
      "multiple_choice",
      "number",
      "text_or_file",
      "text_with_file",
      "file_with_text",
    ]),
    role_type: z.enum(["INVESTOR", "FUNDRAISER"]),
    is_active: z.boolean().optional(),
    metadata: z
      .object({
        choices: z.array(z.string()).optional(),
        file_types: z.array(z.string()).optional(),
        max_file_size_mb: z.number().optional(),
        min_value: z.number().optional(),
        max_value: z.number().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .refine(
    (data) => {
      if (data.data_type === "multiple_choice") {
        return data.metadata?.choices && data.metadata.choices.length > 0;
      }
      return true;
    },
    {
      message: "At least one choice is required for multiple choice questions",
      path: ["metadata", "choices"],
    }
  );

type TQuestionFormValues = z.infer<typeof questionFormSchema>;

type TQuestionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question?: TOnboardingQuestion;
  roleType: "INVESTOR" | "FUNDRAISER";
  onSave: (data: TCreateOnboardingQuestionRequest | TUpdateOnboardingQuestionRequest) => Promise<void>;
};

export function QuestionModal({ open, onOpenChange, question, roleType, onSave }: TQuestionModalProps) {
  const [choiceInput, setChoiceInput] = useState("");
  const [fileTypesInput, setFileTypesInput] = useState("");
  const isEditing = !!question;

  const form = useForm<TQuestionFormValues>({
    resolver: zodResolver(questionFormSchema),
    defaultValues: {
      question_text: "",
      question_description: "",
      data_type: "text",
      role_type: roleType,
      is_active: true,
      metadata: null,
    },
  });

  const dataType = useWatch({ control: form.control, name: "data_type" });
  const metadata = useWatch({ control: form.control, name: "metadata" });

  // Get choices from watched metadata, with fallback to question metadata (for editing)
  // Handle both question_metadata (from API) and metadata (transformed)
  const formChoices = metadata?.choices && Array.isArray(metadata.choices) ? metadata.choices : [];
  const questionWithMetadata = question as
    | (TOnboardingQuestion & { question_metadata?: TOnboardingQuestion["metadata"] })
    | undefined;
  const questionMetadata = questionWithMetadata?.question_metadata ?? question?.metadata;
  const questionChoices =
    questionMetadata?.choices && Array.isArray(questionMetadata.choices) ? questionMetadata.choices : [];
  const choices = formChoices.length > 0 ? formChoices : questionChoices;

  useEffect(() => {
    if (question && open) {
      // Ensure metadata is properly structured, especially for multiple choice questions
      // Handle both question_metadata (from API) and metadata (transformed)
      const questionWithMetadata = question as TOnboardingQuestion & {
        question_metadata?: TOnboardingQuestion["metadata"];
      };
      let processedMetadata = questionWithMetadata.question_metadata ?? question.metadata;

      if (question.data_type === "multiple_choice") {
        if (processedMetadata && typeof processedMetadata === "object" && processedMetadata !== null) {
          // Preserve all existing metadata properties and ensure choices array exists
          const choicesArray = Array.isArray(processedMetadata.choices) ? processedMetadata.choices : [];
          processedMetadata = {
            ...processedMetadata,
            choices: choicesArray,
          };
        } else {
          // Initialize metadata with empty choices array if it doesn't exist
          processedMetadata = {
            choices: [],
          };
        }
      }

      const resetData = {
        question_text: question.question_text,
        question_description: question.question_description ?? "",
        data_type: question.data_type,
        role_type: question.role_type,
        is_active: question.is_active,
        metadata: processedMetadata ?? null,
      };

      form.reset(resetData);

      // Explicitly set metadata after reset to ensure it's properly registered
      // This is important for react-hook-form to properly track nested values
      if (processedMetadata && typeof processedMetadata === "object") {
        form.setValue("metadata", processedMetadata, { shouldValidate: false, shouldDirty: false });
      }

      setChoiceInput("");
      setFileTypesInput(processedMetadata?.file_types?.join(",") ?? "");
    } else if (!question && open) {
      form.reset({
        question_text: "",
        question_description: "",
        data_type: "text",
        role_type: roleType,
        is_active: true,
        metadata: null,
      });
      setChoiceInput("");
      setFileTypesInput("");
    }
  }, [question, form, roleType, open]);

  const handleAddChoice = useCallback(() => {
    if (choiceInput.trim()) {
      const currentMetadata = form.getValues("metadata") ?? {};
      const currentChoices = currentMetadata.choices ?? [];
      form.setValue("metadata", {
        ...currentMetadata,
        choices: [...currentChoices, choiceInput.trim()],
      });
      setChoiceInput("");
    }
  }, [choiceInput, form]);

  const handleRemoveChoice = useCallback(
    (index: number) => {
      const currentMetadata = form.getValues("metadata") ?? {};
      const currentChoices = currentMetadata.choices ?? [];
      const newChoices = currentChoices.filter((_, i) => i !== index);
      form.setValue("metadata", {
        ...currentMetadata,
        choices: newChoices,
      });
    },
    [form]
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      await form.handleSubmit(async (payload) => {
        // Clean up metadata - remove empty fields
        const cleanedMetadata = payload.metadata
          ? Object.fromEntries(
              Object.entries(payload.metadata).filter(([_, value]) => {
                if (Array.isArray(value)) return value.length > 0;
                return value !== null && value !== undefined && value !== "";
              })
            )
          : null;

        const submitData = {
          ...payload,
          metadata: Object.keys(cleanedMetadata ?? {}).length > 0 ? cleanedMetadata : null,
        };

        await onSave(submitData);
        onOpenChange(false);
      })();
    },
    [form, onSave, onOpenChange]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Question" : "New Question"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update the onboarding question details." : "Create a new onboarding question."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="question_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Question Text *</FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-[80px] rounded-md border border-input"
                      placeholder="Enter the question text..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="question_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sub Text / Description</FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-[60px] rounded-md border border-input"
                      placeholder="Enter additional description or guidance..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="data_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data Type *</FormLabel>
                  <FormControl>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      {...field}
                    >
                      <option value="text">Text</option>
                      <option value="file_upload">File Upload</option>
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="number">Number</option>
                      <option value="text_or_file">Text or File</option>
                      <option value="text_with_file">Text with File (optional)</option>
                      <option value="file_with_text">File with Text (optional)</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Multiple Choice Options */}
            {dataType === "multiple_choice" && (
              <div className="space-y-2">
                <FormLabel>Choices *</FormLabel>
                <div className="space-y-2">
                  {choices.map((choice, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2"
                    >
                      <Input
                        value={choice}
                        readOnly
                        className="flex-1 rounded-md border border-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveChoice(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <Input
                      value={choiceInput}
                      onChange={(e) => setChoiceInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddChoice();
                        }
                      }}
                      placeholder="Enter a choice option..."
                      className="flex-1 rounded-md border border-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleAddChoice}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {form.formState.errors.metadata?.choices && (
                  <p className="text-sm text-destructive">{form.formState.errors.metadata.choices.message}</p>
                )}
              </div>
            )}

            {/* File Upload Metadata */}
            {(dataType === "file_upload" || dataType === "text_with_file" || dataType === "file_with_text") && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="metadata.file_types"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Allowed File Types</FormLabel>
                      <FormControl>
                        <Input
                          className="rounded-md border border-input"
                          placeholder="e.g., pdf,doc,docx (comma-separated)"
                          value={fileTypesInput}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            setFileTypesInput(rawValue);
                            // Process the value: split by comma, trim, and filter empty strings
                            const processedValue = rawValue
                              .split(",")
                              .map((v) => v.trim())
                              .filter((v) => v.length > 0);
                            field.onChange(processedValue.length > 0 ? processedValue : undefined);
                          }}
                          onBlur={() => {
                            // On blur, ensure the display value matches the processed value
                            const processedValue = fileTypesInput
                              .split(",")
                              .map((v) => v.trim())
                              .filter((v) => v.length > 0);
                            if (processedValue.length > 0) {
                              setFileTypesInput(processedValue.join(","));
                            } else {
                              setFileTypesInput("");
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="metadata.max_file_size_mb"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max File Size (MB)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          className="rounded-md border border-input"
                          placeholder="e.g., 10"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value ? Number(e.target.value) : undefined;
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Number Metadata */}
            {dataType === "number" && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="metadata.min_value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum Value</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          className="rounded-md border border-input"
                          placeholder="e.g., 0"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value ? Number(e.target.value) : undefined;
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="metadata.max_value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maximum Value</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          className="rounded-md border border-input"
                          placeholder="e.g., 1000000"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value ? Number(e.target.value) : undefined;
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Active Status Toggle */}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value ?? true}
                      onCheckedChange={(checked) => {
                        // Handle boolean or "indeterminate" from Radix UI
                        field.onChange(checked === true);
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active Question</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Inactive questions will be displayed at the bottom and won't be shown to users during onboarding.
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <FormButton>{isEditing ? "Update" : "Create"}</FormButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
